import { OrganisationTypeConstants } from "../../../../../../constants";
import { logError } from "../../../../../../service/error";
import { SearchWorkflowTasksRequest, SearchWorkflowTasksResponse } from "../../../../../../service/query";
import { getTaskBoard, searchWorkflowTasks } from "../../../../../../service/query/QueryService.full";
import { Status } from "../../../../../../service/Shared";
import { resolveTaskParentKind } from "../../../../../../templates/WorkflowTaskPanel/taskParentPolicy";
import {
  getDealListingRoute,
  getLandholdingTasksRoute,
  getProjectTasksRoute,
  getWorkflowTasksBoardRoute,
} from "../../../../../../utils/routes";

type TaskResult = SearchWorkflowTasksResponse["results"][number];

// The route helpers already append ?origin= for some callers, so the param is joined rather than assumed first.
const withTaskUuid = (route: string, taskUuid: string | undefined): string =>
  taskUuid ? `${route}${route.includes("?") ? "&" : "?"}taskUuid=${taskUuid}` : route;

const findTask = async (filter: SearchWorkflowTasksRequest["filter"]): Promise<TaskResult | undefined> => {
  const response = await searchWorkflowTasks({
    paging: { afterCursor: null, beforeCursor: null, limit: 1 },
    filter,
  });

  return response.status === Status.Success ? response.data?.results[0] : undefined;
};

/*
  A deal has no tasks tab, so its general tasks are only listed on the deal workflow board's General
  view — which means resolving the deal's workflow first. `showComplete` is true because we want the
  workflow whatever state its tasks are in; only its uuid is read. A deal with no workflow has nowhere
  to list tasks at all, so the deal itself is the closest thing to its task's parent page.
*/
const getDealGeneralTasksRoute = async (
  dealUuid: string,
  userType: OrganisationTypeConstants,
  taskUuid: string | undefined
): Promise<string | undefined> => {
  const response = await getTaskBoard({
    showComplete: true,
    paging: { afterCursor: null, beforeCursor: null, limit: 1 },
    filter: { results: { dealUuid: { operator: "eq", value: dealUuid } } },
  });

  const dealWorkflowUuid = response.status === Status.Success ? response.data?.results[0]?.uuid : undefined;
  if (!dealWorkflowUuid) return withTaskUuid(getDealListingRoute(dealUuid, userType), taskUuid);

  return withTaskUuid(`${getWorkflowTasksBoardRoute(dealWorkflowUuid, userType)}?view=general`, taskUuid);
};

// Where the tasks of a parent object are listed. `taskUuid` deep-links the task's overlay open on arrival.
const getParentTasksRoute = async (
  objectType: string | null,
  objectUuid: string,
  userType: OrganisationTypeConstants,
  taskUuid: string | undefined
): Promise<string | undefined> => {
  switch (resolveTaskParentKind(objectType)) {
    case "project":
      return withTaskUuid(getProjectTasksRoute(objectUuid, userType), taskUuid);
    case "landholding":
      return withTaskUuid(getLandholdingTasksRoute(objectUuid, userType), taskUuid);
    case "deal":
      return getDealGeneralTasksRoute(objectUuid, userType, taskUuid);
    default:
      return undefined;
  }
};

/*
  A task records its parent on objectType/objectUuid, and the object its workflow hangs off on
  workflowObjectType/workflowObjectUuid (see taskParentPolicy). Either can name the parent — objectType
  also carries "Activity" for an activity-related task — so both are tried, most specific first.
*/
const getTaskParentRoute = async (
  task: TaskResult,
  userType: OrganisationTypeConstants,
  taskUuid: string | undefined
): Promise<string | undefined> => {
  if (task.objectUuid) {
    const objectRoute = await getParentTasksRoute(task.objectType, task.objectUuid, userType, taskUuid);
    if (objectRoute) return objectRoute;
  }

  if (task.workflowObjectUuid) {
    const workflowObjectRoute = await getParentTasksRoute(
      task.workflowObjectType,
      task.workflowObjectUuid,
      userType,
      taskUuid
    );
    if (workflowObjectRoute) return workflowObjectRoute;
  }

  return undefined;
};

/*
  KAN-9609: a general task has no workflow, so the notification raised for one links to
  /workflows/tasks-board/<uuid> carrying something that is not a workflow uuid, leaving the user on an
  undefined tasks board. When the route uuid names no workflow, it is reinterpreted here — in order of
  how precisely each reading identifies the task, so the best available target wins:

    1. the task's own uuid      → wherever that task is listed, with ?taskUuid= opening its overlay
    2. a parent object's uuid   → that object's tasks list (no single task to surface)
    3. a workflow object's uuid → that object's workflow board

  Returns undefined when none of those match, so the caller can surface a not-found rather than
  navigate somewhere arbitrary.
*/
export const resolveTasksBoardFallbackRoute = async (
  uuid: string,
  userType: OrganisationTypeConstants
): Promise<string | undefined> => {
  const task = await findTask({ results: { uuid: { operator: "eq", value: uuid } } });
  if (task) {
    /*
      A stepped task lives on its workflow board, so that is home even though its parent object also lists
      it. Only a general task — which never has a workflow — falls through to the parent's tasks list.
    */
    if (task.workflowUuid) {
      return withTaskUuid(getWorkflowTasksBoardRoute(task.workflowUuid, userType), task.uuid);
    }

    const parentRoute = await getTaskParentRoute(task, userType, task.uuid);
    if (parentRoute) return parentRoute;

    /*
      The task exists but names no parent this app can route to, and has no workflow either. Logged with
      the fields the decision turned on: a not-found toast alone leaves nothing to diagnose from.
    */
    logError({
      error:
        `In 'tasksBoardFallbackRoute', task ${uuid} could not be placed: ` +
        `objectType=${task.objectType}, objectUuid=${task.objectUuid}, ` +
        `workflowObjectType=${task.workflowObjectType}, workflowObjectUuid=${task.workflowObjectUuid}`,
    });
    return undefined;
  }

  const taskOnObject = await findTask({ results: { objectUuid: { operator: "eq", value: uuid } } });
  if (taskOnObject) return getParentTasksRoute(taskOnObject.objectType, uuid, userType, undefined);

  const taskOnWorkflowObject = await findTask({ results: { workflowObjectUuid: { operator: "eq", value: uuid } } });
  if (taskOnWorkflowObject?.workflowUuid) {
    return getWorkflowTasksBoardRoute(taskOnWorkflowObject.workflowUuid, userType);
  }

  // Distinguished from an unplaceable task above: here the uuid matched nothing the user can read at all.
  logError({ error: `In 'tasksBoardFallbackRoute', uuid ${uuid} matched no workflow and no task` });
  return undefined;
};
